import type { ErrorComponentProps } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { RotateCcw } from "lucide-react";
import { motion } from "motion/react";

import { Button } from "@/components/ui/button";
import { DatabaseError, ForbiddenError } from "@/lib/errors";

const containerVariants = {
	hidden: {},
	visible: {
		transition: { staggerChildren: 0.1, delayChildren: 0.15 },
	},
};

const itemVariants = {
	hidden: { opacity: 0, y: 18 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as const },
	},
};

export function ErrorPage({ error, reset }: ErrorComponentProps) {
	const isForbidden = error instanceof ForbiddenError;
	const isDatabase = error instanceof DatabaseError;
	const statusCode = isForbidden ? 403 : 500;

	const title = isForbidden ? "无权访问" : "Oops!";
	const subtitle = isForbidden
		? "你没有权限访问此页面"
		: isDatabase
			? "服务暂时不可用"
			: "出了点问题";
	const detail = isDatabase
		? "数据库操作失败，请稍后再试。"
		: isForbidden
			? error.message
			: "发生了未知错误，请稍后再试。";

	return (
		<div className="flex min-h-screen w-full flex-col items-center justify-center bg-background px-4 py-12">
			<motion.div
				className="flex flex-col items-center"
				variants={containerVariants}
				initial="hidden"
				animate="visible"
			>
				<motion.div
					variants={itemVariants}
					className="font-black text-[8rem] leading-none tracking-tighter text-foreground sm:text-[10rem]"
				>
					{statusCode}
				</motion.div>

				<motion.h1
					variants={itemVariants}
					className="mt-4 font-black text-3xl tracking-tight text-foreground sm:text-4xl"
				>
					{title}
				</motion.h1>

				<motion.p
					variants={itemVariants}
					className="mt-2 font-semibold text-base text-muted-foreground"
				>
					{subtitle}
				</motion.p>

				<motion.p
					variants={itemVariants}
					className="mt-2 max-w-sm text-center text-sm text-muted-foreground/70"
				>
					{detail}
				</motion.p>

				<motion.div variants={itemVariants} className="mt-8 flex gap-3">
					{!isForbidden && (
						<Button size="lg" className="rounded-xl px-5" onClick={reset}>
							<RotateCcw /> 重试
						</Button>
					)}
					<Button
						variant={isForbidden ? "default" : "outline"}
						size="lg"
						className="rounded-xl px-5"
						render={<Link to="/">返回首页</Link>}
						nativeButton={false}
					/>
				</motion.div>
			</motion.div>
		</div>
	);
}
